/**
 * =========================================================
 * Social Media Hub - Premium Global Styles
 * =========================================================
 *
 * Modern, polished global styles with smooth animations,
 * glass morphism effects, and premium visual aesthetics.
 */

// Material Dashboard 2 React Base Styles
import colors from "assets/theme/base/colors";

const { primary, dark, grey } = colors;

const globals = {
  // Import Inter font from Google Fonts with all weights
  "@import":
    "url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap')",

  html: {
    scrollBehavior: "smooth",
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    textRendering: "optimizeLegibility",
  },

  "*, *::before, *::after": {
    margin: 0,
    padding: 0,
    boxSizing: "border-box",
  },

  body: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: "16px",
    lineHeight: 1.6,
    color: dark.main,
    backgroundColor: grey[50],
    overflowX: "hidden",
  },

  // Root app container
  "#app": {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },

  "a, a:link, a:visited": {
    textDecoration: "none !important",
  },

  "a.link, .link, a.link:link, .link:link, a.link:visited, .link:visited": {
    color: `${dark.main} !important`,
    transition: "color 150ms ease-in !important",
  },

  "a.link:hover, .link:hover, a.link:focus, .link:focus": {
    color: `${primary.main} !important`,
  },

  // Smooth focus styles
  "*:focus": {
    outline: "none",
  },

  "*:focus-visible": {
    outline: `2px solid ${primary.main}`,
    outlineOffset: "2px",
  },

  // Premium Custom scrollbar
  "::-webkit-scrollbar": {
    width: "6px",
    height: "6px",
  },

  "::-webkit-scrollbar-track": {
    background: "transparent",
  },

  "::-webkit-scrollbar-thumb": {
    background: grey[300],
    borderRadius: "10px",
    transition: "background 0.2s ease",

    "&:hover": {
      background: grey[400],
    },
  },

  // Hide scrollbar when not hovering (for cleaner look)
  "*:not(:hover)": {
    "::-webkit-scrollbar-thumb": {
      background: "transparent",
    },
  },

  // Selection color
  "::selection": {
    backgroundColor: `${primary.main}25`,
    color: dark.main,
  },

  // Placeholder styling
  "::-webkit-input-placeholder": {
    color: grey[400],
    opacity: 1,
  },

  "::-moz-placeholder": {
    color: grey[400],
    opacity: 1,
  },

  ":-ms-input-placeholder": {
    color: grey[400],
    opacity: 1,
  },

  // Remove default button styles
  "button, input, textarea, select": {
    fontFamily: "inherit",
    fontSize: "inherit",
  },

  // Smooth transitions for interactive elements
  "button, a, input, textarea, select, [role='button']": {
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  },

  // Better image rendering
  img: {
    maxWidth: "100%",
    height: "auto",
    display: "block",
  },

  // Code blocks
  "pre, code": {
    fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
  },

  // Remove list styles
  "ul, ol": {
    listStyle: "none",
  },

  // Table defaults
  table: {
    borderCollapse: "collapse",
    width: "100%",
  },

  // Premium Animation keyframes
  "@keyframes fadeIn": {
    "0%": {
      opacity: 0,
    },
    "100%": {
      opacity: 1,
    },
  },

  "@keyframes fadeInUp": {
    "0%": {
      opacity: 0,
      transform: "translateY(20px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },

  "@keyframes fadeInDown": {
    "0%": {
      opacity: 0,
      transform: "translateY(-20px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },

  "@keyframes slideUp": {
    "0%": {
      opacity: 0,
      transform: "translateY(10px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },

  "@keyframes slideInRight": {
    "0%": {
      opacity: 0,
      transform: "translateX(20px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateX(0)",
    },
  },

  "@keyframes slideInLeft": {
    "0%": {
      opacity: 0,
      transform: "translateX(-20px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateX(0)",
    },
  },

  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.5,
    },
  },

  "@keyframes spin": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },

  "@keyframes shimmer": {
    "0%": {
      backgroundPosition: "-200% 0",
    },
    "100%": {
      backgroundPosition: "200% 0",
    },
  },

  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(0)",
    },
    "50%": {
      transform: "translateY(-10px)",
    },
  },

  "@keyframes glow": {
    "0%, 100%": {
      boxShadow: `0 0 5px ${primary.main}40`,
    },
    "50%": {
      boxShadow: `0 0 20px ${primary.main}60`,
    },
  },

  "@keyframes scaleIn": {
    "0%": {
      opacity: 0,
      transform: "scale(0.9)",
    },
    "100%": {
      opacity: 1,
      transform: "scale(1)",
    },
  },

  "@keyframes bounceIn": {
    "0%": {
      opacity: 0,
      transform: "scale(0.3)",
    },
    "50%": {
      transform: "scale(1.05)",
    },
    "70%": {
      transform: "scale(0.9)",
    },
    "100%": {
      opacity: 1,
      transform: "scale(1)",
    },
  },
};

export default globals;
